// Per-user undo/redo (spec §5.8). Each entry is a list of inverse ops captured
// at the time of change; an entry is undone by applying it in reverse. Ops made
// between begin() and end() form one gesture = one entry.
import type { ItemOp, Op } from '../../shared/types.ts';

export type ApplyFn = (ops: readonly Op[]) => Op[];

const LIMIT = 500;

export class History {
  private readonly applyOps: ApplyFn;
  private undoStack: Op[][] = [];
  private redoStack: Op[][] = [];
  private open: Op[] | null = null;
  private depth = 0;
  // Coalescing state for the open gesture.
  private createdInGesture = new Set<string>();
  private updateIndex = new Map<string, number>();
  private itemIndex = new Map<string, Map<string, number>>(); // card id → item id → entry index

  constructor(apply: ApplyFn) {
    this.applyOps = apply;
  }

  get canUndo(): boolean { return this.undoStack.length > 0; }
  get canRedo(): boolean { return this.redoStack.length > 0; }
  get inGesture(): boolean { return this.open !== null; }
  /** How many begin()s are open (1 = the outermost gesture). */
  get nesting(): number { return this.depth; }
  get sizes(): [number, number] { return [this.undoStack.length, this.redoStack.length]; }

  begin(): void {
    if (this.depth++ === 0) {
      this.open = [];
      this.createdInGesture.clear();
      this.updateIndex.clear();
      this.itemIndex.clear();
    }
  }

  end(): void {
    if (this.depth === 0) return;
    if (--this.depth > 0) return;
    const entry = this.open!;
    this.open = null;
    if (entry.length) this.push(entry);
  }

  /** Reverts everything recorded in the open gesture and discards it. */
  cancel(): void {
    if (!this.open) return;
    const entry = this.open;
    this.open = null;
    this.depth = 0;
    this.applyOps(entry.slice().reverse());
  }

  /** Records inverse ops (in application order) for ops that were just applied. */
  record(inverses: readonly Op[]): void {
    if (!inverses.length) return;
    if (!this.open) {
      this.push([...inverses]);
      return;
    }
    for (const inv of inverses) this.coalesce(this.open, inv);
  }

  /** Undoes the last entry; returns the ops it applied, or null if there was nothing to undo. */
  undo(): readonly Op[] | null {
    const entry = this.undoStack.pop();
    if (!entry) return null;
    const ops = entry.slice().reverse();
    this.redoStack.push(this.applyOps(ops));
    return ops;
  }

  /** Redoes the last undone entry; returns the ops it applied, or null if there was nothing to redo. */
  redo(): readonly Op[] | null {
    const entry = this.redoStack.pop();
    if (!entry) return null;
    const ops = entry.slice().reverse();
    this.undoStack.push(this.applyOps(ops));
    return ops;
  }

  clear(): void {
    this.undoStack = [];
    this.redoStack = [];
  }

  private push(entry: Op[]): void {
    this.undoStack.push(entry);
    if (this.undoStack.length > LIMIT) this.undoStack.shift();
    this.redoStack = [];
  }

  /**
   * Keeps a gesture's entry small: a drag that updates x/y every frame stores
   * one inverse holding the pre-gesture values, not one per frame.
   */
  private coalesce(entry: Op[], inv: Op): void {
    switch (inv.t) {
      case 'delete':
        // Object was created during this gesture; undoing deletes it, so later
        // updates to it need no inverse.
        this.createdInGesture.add(inv.id);
        entry.push(inv);
        return;
      case 'create':
        // Object was deleted during this gesture.
        this.updateIndex.delete(inv.obj.id);
        this.itemIndex.delete(inv.obj.id);
        entry.push(inv);
        return;
      case 'item': {
        if (this.createdInGesture.has(inv.id)) return;
        let byItem = this.itemIndex.get(inv.id);
        const idx = byItem?.get(inv.itemId);
        const existing = idx === undefined ? null : (entry[idx] as ItemOp);
        // The item was created during this gesture: undoing removes it.
        if (existing && existing.props === null) return;
        if (existing?.props && inv.props) {
          const props = existing.props as Record<string, unknown>;
          for (const [k, v] of Object.entries(inv.props)) if (!(k in props)) props[k] = v;
          return;
        }
        if (!byItem) this.itemIndex.set(inv.id, (byItem = new Map()));
        byItem.set(inv.itemId, entry.length);
        entry.push(inv);
        return;
      }
      case 'update': {
        if (this.createdInGesture.has(inv.id)) return;
        const idx = this.updateIndex.get(inv.id);
        if (idx === undefined) {
          this.updateIndex.set(inv.id, entry.length);
          entry.push(inv);
          return;
        }
        // The earliest inverse for each prop holds the pre-gesture value; it is
        // applied last on undo, so merging into it is order-safe.
        const existing = entry[idx] as Extract<Op, { t: 'update' }>;
        const props = existing.props as Record<string, unknown>;
        for (const [k, v] of Object.entries(inv.props)) if (!(k in props)) props[k] = v;
        return;
      }
    }
  }
}
